import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Outlet } from "react-router-dom";
import { LinearProgress } from "@mui/material/";
import { useMsal } from "@azure/msal-react";
import config from "../config";
import actions from "../Store/actions";
import { Screen } from "../common/components";
import { loginRequest } from "../config/authConfig";
import Routes from "./Routes";
import UserOrganisationDialog from "./UserOrganisationDialog";
import LoadingView from "./LoadingView";
import { logToSentry } from "../config/Sentry";
import { strings } from "../common/i18n";

/**
 * MSAL Auth Component
 * Handles authentication state and loads user session for MSAL/B2C
 */
const MsalAuth = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { accounts, instance, inProgress } = useMsal();
    const [msalLoading, setMsalLoading] = React.useState(true);
    const [sessionLoaded, setSessionLoaded] = React.useState(false);
    const [isHandlingError, setIsHandlingError] = React.useState(false);
    const fetchAttempted = useRef(false);
    const authRequestDispatched = useRef(false); // Track if we've dispatched AUTH_GET_REQUEST for current attempt
    const [appLoading, setAppLoading] = useState(true);
    const { userOrganisations } = useSelector((state) => state);

    // Get auth state from Redux
    const auth = useSelector((state) => state.auth);

    const isAuthenticated = accounts.length > 0;
    const account = accounts[0];

    useEffect(() => {
        // Wait for MSAL to finish any in-progress operations
        if (inProgress !== "none") {
            return;
        }

        // Don't redirect if we're handling an error (let the error handler take over)
        if (isHandlingError) {
            return;
        }

        // If not authenticated, redirect to login
        if (!isAuthenticated) {
            fetchAttempted.current = false;
            navigate(`/${config.paths.login}`, { replace: true });
            return;
        }

        // Prevent multiple fetch attempts
        if (fetchAttempted.current || sessionLoaded) {
            return;
        }

        fetchAttempted.current = true;
        authRequestDispatched.current = false; // Reset flag - we haven't dispatched request yet

        // Reset error handling flag when starting a new authentication attempt
        setIsHandlingError(false);

        // Clear any previous backend auth error flags when starting a new authentication attempt
        // This prevents stale error flags from previous failed logins from affecting new attempts
        localStorage.removeItem("backend_auth_failed");
        localStorage.removeItem("backend_auth_error");

        // Load user session with MSAL account data
        const fetchData = async () => {
            try {
                // Acquire B2C token using MSAL (proper way)
                const tokenRequest = {
                    ...loginRequest,
                    account: account
                };

                // Add timeout handling for acquireTokenSilent
                const tokenPromise = instance.acquireTokenSilent(tokenRequest);
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => {
                        reject(new Error("Token acquisition timeout"));
                    }, 25000); // 25 second timeout
                });

                const response = await Promise.race([
                    tokenPromise,
                    timeoutPromise
                ]);
                const b2cIdToken = response.idToken;

                if (!b2cIdToken) {
                    throw new Error(
                        "No B2C token received from acquireTokenSilent"
                    );
                }

                // Exchange B2C token for backend custom token
                // Dispatch the auth request (saga will handle it asynchronously)
                // This will clear any previous error in the reducer (AUTH_GET_REQUEST sets error: null)
                dispatch(
                    actions.auth.create.getRequest({
                        username: account.username,
                        idToken: b2cIdToken
                    })
                );

                // Mark that we've dispatched the request - any errors after this are from current attempt
                // This must be set synchronously before the saga runs to ensure error handler can detect it
                authRequestDispatched.current = true;

                // Set msalLoading to false since token acquisition is complete
                // The backend saga will run asynchronously, but we're no longer "loading" the MSAL token
                setMsalLoading(false);

                // Note: The saga runs async and updates Redux state
                // The component will re-render when auth state changes
                // The AUTH_GET_REQUEST action clears the error state, so any error after this
                // is from the current attempt, not a stale error from a previous attempt
            } catch (error) {
                // Handle timeout errors specifically
                if (
                    error.message === "Token acquisition timeout" ||
                    error.errorCode === "timed_out" ||
                    (error.name === "BrowserAuthError" &&
                        error.errorMessage?.includes("timed_out"))
                ) {
                    logToSentry("MSAL token acquisition timeout", {
                        error: {
                            name: error?.name,
                            message: error?.message,
                            errorCode: error?.errorCode
                        },
                        username: account?.username
                    });
                    fetchAttempted.current = false;
                    setMsalLoading(false);
                    localStorage.setItem("session_expired", "true");
                    navigate(`/${config.paths.login}`, { replace: true });
                    return;
                }

                // If acquireTokenSilent fails, clear the stale account and force fresh login
                if (
                    error.name === "InteractionRequiredAuthError" ||
                    error.errorCode === "interaction_required"
                ) {
                    logToSentry(
                        "MSAL interaction required - forcing fresh login",
                        {
                            error: {
                                name: error?.name,
                                message: error?.message,
                                errorCode: error?.errorCode
                            },
                            username: account?.username
                        }
                    );

                    // Clear the stale MSAL account
                    try {
                        await instance.clearCache();
                    } catch (clearError) {
                        logToSentry(
                            "Failed to clear MSAL cache after interaction required",
                            {
                                error: {
                                    name: clearError?.name,
                                    message: clearError?.message,
                                    errorCode: clearError?.errorCode
                                }
                            }
                        );
                    }

                    // Force a fresh login by redirecting directly to Azure
                    instance
                        .loginRedirect(loginRequest)
                        .catch((redirectError) => {
                            logToSentry(
                                "Login redirect failed after interaction required",
                                {
                                    error: {
                                        name: redirectError?.name,
                                        message: redirectError?.message,
                                        errorCode: redirectError?.errorCode
                                    }
                                }
                            );
                            fetchAttempted.current = false;
                            navigate(`/${config.paths.login}`, {
                                replace: true
                            });
                        });
                    // Don't do anything else, browser will redirect
                } else {
                    // For other errors, clear everything and redirect to login
                    logToSentry("MSAL session load failed", {
                        error: {
                            name: error?.name,
                            message: error?.message,
                            errorCode: error?.errorCode,
                            errorMessage: error?.errorMessage
                        },
                        username: account?.username
                    });
                    fetchAttempted.current = false;
                    setMsalLoading(false);
                    localStorage.setItem("session_expired", "true");
                    navigate(`/${config.paths.login}`, { replace: true });
                }
            }
        };

        fetchData();
    }, [
        isAuthenticated,
        inProgress,
        account,
        sessionLoaded,
        isHandlingError,
        instance,
        dispatch,
        navigate
    ]);

    // Watch Redux auth state and mark session as loaded when populated or handle errors
    useEffect(() => {
        // If auth saga failed, force full logout and fresh login
        // Only handle error if:
        // 1. Error exists
        // 2. Not currently loading (request completed)
        // 3. We've attempted to fetch (fetchAttempted is true)
        // 4. Not already handling an error
        // 5. We're not in the middle of acquiring a token (msalLoading is false means we've completed token acquisition)
        // 6. We've dispatched AUTH_GET_REQUEST for the current attempt (authRequestDispatched is true)
        // This prevents handling stale errors from previous failed login attempts
        // Only handle errors if they occur after we've dispatched AUTH_GET_REQUEST (which clears previous errors)
        if (
            auth?.error &&
            !auth?.loading &&
            fetchAttempted.current &&
            !isHandlingError &&
            !msalLoading &&
            authRequestDispatched.current
        ) {
            // Set flag to prevent first useEffect from interfering
            setIsHandlingError(true);

            // Store error details for debugging (survives navigation)
            const errorDetails = {
                error: auth.error.toString
                    ? auth.error.toString()
                    : String(auth.error),
                timestamp: new Date().toISOString(),
                username: auth.username || account?.username || "unknown"
            };

            // Clear everything
            fetchAttempted.current = false;
            setMsalLoading(false);

            // Set flags (use localStorage for better persistence across redirects)
            localStorage.setItem("backend_auth_failed", "true");
            localStorage.setItem(
                "backend_auth_error",
                JSON.stringify(errorDetails)
            );

            // Clear MSAL cache and navigate to login
            instance
                .clearCache()
                .then(() => {
                    navigate(`/${config.paths.login}`, { replace: true });
                })
                .catch((error) => {
                    logToSentry(
                        "Failed to clear MSAL cache on auth saga error",
                        {
                            error: {
                                name: error?.name,
                                message: error?.message,
                                errorCode: error?.errorCode
                            },
                            authError: auth?.error
                                ? String(auth.error)
                                : "unknown"
                        }
                    );
                    navigate(`/${config.paths.login}`, { replace: true });
                });
            return;
        }

        // Check if Redux auth state is populated successfully
        if (auth && auth.isAuthenticated && (auth.account || auth.username)) {
            // Clear any previous backend auth error flags (in case of successful re-login)
            localStorage.removeItem("backend_auth_failed");
            localStorage.removeItem("backend_auth_error");

            setSessionLoaded(true);
            setMsalLoading(false);
            return;
        }

        // Safety timeout: if auth saga doesn't respond within 30 seconds, redirect to login
        const timeout = setTimeout(() => {
            if (!sessionLoaded && msalLoading && fetchAttempted.current) {
                logToSentry("Auth saga timeout - no response after 30s", {
                    username: account?.username,
                    accountsCount: accounts?.length
                });

                // Clear everything and redirect to login
                fetchAttempted.current = false;
                setMsalLoading(false);
    
                // Clear MSAL cache and force fresh login
                instance
                    .clearCache()
                    .then(() => {
                        navigate(`/${config.paths.login}`, { replace: true });
                    })
                    .catch((error) => {
                        logToSentry("Failed to clear MSAL cache on timeout", {
                            error: {
                                name: error?.name,
                                message: error?.message,
                                errorCode: error?.errorCode
                            }
                        });
                        navigate(`/${config.paths.login}`, { replace: true });
                    });
            }
        }, 30000);

        return () => clearTimeout(timeout);
    }, [
        auth,
        sessionLoaded,
        msalLoading,
        isAuthenticated,
        isHandlingError,
        account,
        accounts,
        fetchAttempted,
        instance
    ]);

    // Proactive token renewal: periodically check if the token is nearing expiry
    // and silently renew it. This ensures password resets on other devices are detected
    // promptly (within minutes) instead of waiting for the token to fully expire.
    const RENEWAL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
    const RENEWAL_BUFFER_MS = 10 * 60 * 1000; // Renew if expiring within 10 minutes

    const handleTokenRenewalFailure = useCallback(async () => {
        logToSentry("Proactive token renewal failed - session expired", {
            username: account?.username
        });

        try {
            await instance.clearCache();
        } catch (clearError) {
            logToSentry("Failed to clear MSAL cache during proactive renewal", {
                error: {
                    name: clearError?.name,
                    message: clearError?.message
                }
            });
        }

        localStorage.setItem("session_expired", "true");
        navigate(`/${config.paths.login}`, { replace: true });
    }, [instance, account, navigate]);

    useEffect(() => {
        if (!sessionLoaded || !isAuthenticated || !account) {
            return;
        }

        const checkAndRenewToken = async () => {
            try {
                // Check if the current ID token is expiring soon
                const idTokenClaims = account?.idTokenClaims;
                if (idTokenClaims?.exp) {
                    const expiresAt = idTokenClaims.exp * 1000; // Convert to ms
                    const now = Date.now();
                    const timeUntilExpiry = expiresAt - now;

                    // Only renew if token expires within the buffer window
                    if (timeUntilExpiry > RENEWAL_BUFFER_MS) {
                        return;
                    }
                }

                // Attempt silent token renewal
                const tokenRequest = {
                    ...loginRequest,
                    account: account,
                    forceRefresh: true
                };

                await instance.acquireTokenSilent(tokenRequest);
            } catch (error) {
                if (
                    error.name === "InteractionRequiredAuthError" ||
                    error.errorCode === "interaction_required"
                ) {
                    // Refresh token was revoked (e.g. password reset on another device)
                    await handleTokenRenewalFailure();
                } else {
                    logToSentry("Proactive token renewal error", {
                        error: {
                            name: error?.name,
                            message: error?.message,
                            errorCode: error?.errorCode
                        },
                        username: account?.username
                    });
                }
            }
        };

        const intervalId = setInterval(checkAndRenewToken, RENEWAL_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [sessionLoaded, isAuthenticated, account, instance, handleTokenRenewalFailure]);

    // Fetch primary data after MSAL session is loaded
    const fetchPrimaryData = async () => {
        /** Always fetch orgs to validate access; saga will re-select previous org if still available */
        await dispatch(actions.userOrganisations.create.getRequest());
    };

    // Load secondary data when an org is selected
    const fetchSecondaryData = async () => {
        await Promise.all([
            dispatch(actions.branches.create.getRequest()),
            dispatch(actions.branchLocations.create.getRequest()),
            dispatch(actions.locations.create.getRequest()),
            dispatch(actions.userLocations.create.getRequest()),
            dispatch(actions.typesList.create.getRequest())
        ]);
    };

    // Fetch orgs once after session is loaded
    const orgsFetched = useRef(false);
    useEffect(() => {
        if (!sessionLoaded || !auth?.isAuthenticated || orgsFetched.current) {
            return;
        }
        orgsFetched.current = true;

        const fetchData = async () => {
            setAppLoading(true);
            try {
                await fetchPrimaryData();
            } catch (error) {
                logToSentry("On fetching primary data:", { error });
                dispatch(
                    actions.toast.create.error(strings("errors.onPrimaryLoad"))
                );
            }
            setAppLoading(false);
        };

        fetchData();
    }, [sessionLoaded, auth?.isAuthenticated]);

    // Fetch secondary data when org item changes
    useEffect(() => {
        if (!sessionLoaded || !auth?.isAuthenticated || !userOrganisations.item?.id) {
            return;
        }

        const fetchData = async () => {
            setAppLoading(true);
            try {
                await fetchSecondaryData();
            } catch (error) {
                logToSentry("On fetching secondary data:", { error });
                dispatch(
                    actions.toast.create.error(strings("errors.onPrimaryLoad"))
                );
            }
            setAppLoading(false);
        };

        fetchData();
    }, [sessionLoaded, auth?.isAuthenticated, userOrganisations.item?.id]);

    const onUserOrganisationChange = () => {
        navigate(`/${config.paths.auth}/dashboard`);
    };

    // Show loading while waiting for MSAL or fetching session
    if (inProgress !== "none" || !isAuthenticated || msalLoading) {
        return <LinearProgress />;
    }

    // Show loading while fetching app data
    if (appLoading) {
        return <LoadingView />;
    }

    return (
        <>
            {userOrganisations.item && (
                <Screen>
                    <Routes />
                </Screen>
            )}
            <UserOrganisationDialog onChange={onUserOrganisationChange} />
        </>
    );
};

export default MsalAuth;
